/**
 * useStageEditState - Shared hook for pipeline stage editing in action cards.
 *
 * StageAssociationCard and HiddenEntityCard both manage the same stage-edit
 * lifecycle:
 *   1. Track an `editStageId` (the stage entity_id the user is selecting)
 *   2. Detect whether the current value is a legacy stage name (non-UUID)
 *   3. Lazily fetch pipeline stages only when legacy-name resolution is needed
 *   4. Resolve legacy names → entity_ids once stages load
 *   5. Expose a `handleStageChange` callback for the AssociatedPipelineStageInput
 *   6. Expose `resetToProposed` for the cancel handler (restores initial value
 *      and re-arms the legacy-name resolver)
 *
 * Callers retain ownership of dirty-state and save logic, which differs
 * between the two cards.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { StageAssociationValue } from '@/lib/crm/components/properties/inputs/AssociatedPipelineStageInput';
import { usePipelineStages } from '@/lib/crm/hooks/usePipelineStages';

import { isUuid, resolveStageEntityId } from '../stageUtils';

interface UseStageEditStateOptions {
  /** The proposed (or current) stage entity_id — sets the initial state. */
  proposedValue: string | null;
  /** Org context for the pipeline stages fetch. */
  orgId: number | undefined;
  /** Entity type name (e.g. "ahoy_deal") used to scope the pipeline stages query. */
  entityType: string;
}

interface UseStageEditStateResult {
  /** The current stage entity_id being edited (or null for "no stage"). */
  editStageId: string | null;
  /** Pipeline stages fetched for the entity type (empty if the UUID fast path was taken). */
  stages: ReturnType<typeof usePipelineStages>['stages'];
  /** True when the current editStageId is a legacy name needing resolution. */
  isLegacyName: boolean;
  /** Handler for AssociatedPipelineStageInput's onChange. */
  handleStageChange: (assoc: StageAssociationValue | null) => void;
  /** Reset editStageId to the proposed value and re-arm the resolver. */
  resetToProposed: () => void;
}

export function useStageEditState({
  proposedValue,
  orgId,
  entityType,
}: UseStageEditStateOptions): UseStageEditStateResult {
  const [editStageId, setEditStageId] = useState<string | null>(proposedValue);

  // Only fetch stages when the current value looks like a legacy name (non-UUID).
  // UUID values are already entity_ids — no backfill needed, skip the fetch.
  const isLegacyName = useMemo(
    () => !!editStageId && !isUuid(editStageId),
    [editStageId]
  );

  const { stages } = usePipelineStages(
    orgId,
    isLegacyName ? entityType : undefined
  );

  // Resolve legacy stage names (e.g. "ahoy_lead") → entity_ids once stages load.
  const stageResolved = useRef(false);
  useEffect(() => {
    if (stageResolved.current || stages.length === 0 || !editStageId) {
      return;
    }
    stageResolved.current = true;
    if (!stages.some(s => s.entity_id === editStageId)) {
      const resolved = resolveStageEntityId(editStageId, stages);
      if (resolved !== editStageId) {
        setEditStageId(resolved);
      }
    }
  }, [stages, editStageId]);

  // Re-align local edit state when the proposed value changes upstream (e.g. an
  // external refresh/websocket update to the proposal). Without this, editStageId
  // keeps its pre-update value and callers surface a false dirty state. Re-arm the
  // legacy-name resolver so the new value can be resolved again.
  const prevProposedValue = useRef(proposedValue);
  useEffect(() => {
    if (prevProposedValue.current === proposedValue) {
      return;
    }
    prevProposedValue.current = proposedValue;
    stageResolved.current = false;
    setEditStageId(proposedValue);
  }, [proposedValue]);

  const handleStageChange = useCallback(
    (assoc: StageAssociationValue | null) => {
      setEditStageId(assoc?.target_entity_id ?? null);
    },
    []
  );

  const resetToProposed = useCallback(() => {
    setEditStageId(proposedValue);
    // Re-arm the resolver so legacy names can be resolved again after cancel.
    stageResolved.current = false;
  }, [proposedValue]);

  return {
    editStageId,
    stages,
    isLegacyName,
    handleStageChange,
    resetToProposed,
  };
}
